import { format, formatISO, parseISO, type Interval } from 'date-fns'
import { createContext, useContext, useMemo, useRef, useState } from 'react'
import {
  floatingTimestampToDateInZone,
  type IETFLanguageTag,
  WeekStartsOn,
} from '@opallabs/concepts'
import {
  useCalendarDates,
  type UseCalendarDatesValue,
} from './useCalendarDates'

/**
 * Constraints on which dates can be selected. These form the closed interval
 * [start,end]--that is, the user can select the `start` date, or any date up
 * to and including `end`.
 *
 * @note `start` and `end` are coerced to midnight in the user’s local time zone
 * to ensure consistent behavior in all zones. For best results, you may want to
 * ensure they’re already in the user’s time zone when you pass them in. A good
 * option is to call `date-fns`’ `parseISO` with just the date part of an ISO
 * 8601 timestamp.
 */
export type AllowedDates =
  | (Interval & { softDisallow?: SoftDisallowedInterval })
  | (Pick<Interval, 'start'> & {
      end?: never
      softDisallow?: Pick<SoftDisallowedInterval, 'start'> & { end?: never }
    })
  | (Pick<Interval, 'end'> & {
      start?: never
      softDisallow?: { start?: never } & Pick<SoftDisallowedInterval, 'end'>
    })

type SoftDisallowedInterval = {
  /** When true, dates prior to the start of the interval may still be selected. */
  start?: boolean
  /** When true, dates past the end of the interval may still be selected. */
  end?: boolean
}

export type MaybeDate = Date | null
type MaybeTimestamp = string | null

/**
 * The date the user selected.
 */
export type SelectedDate = {
  /**
   * A `Date` representing `dateString`’s date as midnight in the user’s local
   * time zone.
   *
   * @note You are responsible for dealing with cross--time-zone scenarios; if
   * you need the abstract date (that is, an ‘August 30th, 2024’ that’s the same
   * in all time zones) instead of an instant in time in a specific time zone,
   * use `dateString`.
   *
   * @example new Date("2024-08-30T00:00:00-07:00")
   */
  dateObject: Date
  /**
   * An ISO 8601 date representation. Useful if the selected date will not be
   * immediately used to represent an instant in time (for example, if the date
   * is always midnight for every user in any time zone, or if the server
   * determines the correct time and UTC offset).
   *
   * @example "2024-08-30"
   */
  dateString: string
} | null

export type CalendarContextValue = UseCalendarDatesValue & {
  /**
   * Restrict which dates can be selected. See time zone discussion on the
   * `AllowedDates` type.
   *
   * Multiple date intervals can be given in an array. When multiple
   * intervals are used, only dates which are in the intersection of all
   * given intervals are allowed to be selected.
   *
   * Intervals can also be "soft-disallowed", meaning that dates that fall
   * outside of the interval can still be selected, albeit with a warning.
   * You may also control which side of the interval can be soft-disallowed.
   */
  allowedDates?: AllowedDates | AllowedDates[]
  /**
   * Check if a given date is within the bounds of `allowedDates` (if provided).
   * See time zone discussion on the `AllowedDates` type.
   *
   * `allowed` means that the date can be selected; it's either in bounds or
   *  permitted by `softDisallow`.
   * `strictAllowed` means that it strictly falls in the allowed bounds.
   */
  isDateAllowed: (
    date: Date
  ) => readonly [allowed: boolean, strictAllowed: boolean]
  /**
   * Whether the selected date is strictly in bounds of `allowedDates` (if provided).
   * If no date is currently selected, the value is null.
   */
  isSelectedDateStrictlyAllowed: boolean | null
  /**
   * Locale to use for all text displayed in the component. In general it’s best to omit this prop and let the component
   * default to the user’s current locale.
   *
   * @default undefined
   */
  locale?: IETFLanguageTag
  /**
   * Update calendar’s internal state and fire `<CalendarProvider>`’s
   * `onDateSelect` callback with the newly-selected date.
   *
   * @private This is only exposed for `<DateRemover>`, which only passes
   * `null`.
   */
  selectDate: (timestamp: MaybeTimestamp) => void
  /** Currently selected date */
  selectedDate: MaybeDate
  /** React Ref to attach to the <tbody> element representing calendar days */
  tableRef: React.RefObject<HTMLTableElement>
  /** Controls which day of the week appears first */
  weekStartsOn?: WeekStartsOn
}

const CalendarContext = createContext<CalendarContextValue | undefined>(
  undefined
)
CalendarContext.displayName = 'Lapidary: CalendarContext'

export type CalendarProviderOptions = Pick<
  CalendarContextValue,
  'allowedDates' | 'locale' | 'weekStartsOn'
> & {
  initialFocusDate?: Date
  initialSelectedDate?: MaybeDate
  onDateSelect?: (date: SelectedDate) => void
}

function coerceDateForTimeZone(date: Interval['start']): Date {
  return parseISO(format(date, 'yyyy-MM-dd'))
}

function validateInterval({ start, end }: Partial<Interval>): void {
  if (start && end && start > end) {
    throw RangeError(
      `Invalid allowedDates: ${formatISO(start)} is after ${formatISO(end)}`
    )
  }
}

/**
 * Returns a callback that checks if a given date is within the bounds of
 * `allowedDates` (if provided). See time zone discussion on the `AllowedDates`
 * type.
 */
function buildDateValidator(
  allowedDates?: AllowedDates | AllowedDates[]
): CalendarContextValue['isDateAllowed'] {
  if (!allowedDates || (Array.isArray(allowedDates) && !allowedDates.length)) {
    return () => [true, true] as const
  }

  allowedDates = (
    Array.isArray(allowedDates) ? allowedDates : [allowedDates]
  ).map((initial) => {
    validateInterval(initial)

    const interval = { ...initial }
    if (interval.start) {
      interval.start = coerceDateForTimeZone(interval.start)
    }
    if (interval.end) {
      interval.end = coerceDateForTimeZone(interval.end)
    }

    return interval
  })

  return (date) => {
    let isAllowed = true
    let isStrictlyAllowed = true

    for (const { start, end, softDisallow } of allowedDates) {
      const isInterval = start != null && end != null
      const isStartAllowed = (strict: boolean) =>
        start != null && (+date >= +start || (!strict && !!softDisallow?.start))
      const isEndAllowed = (strict: boolean) =>
        end != null && (+date <= +end || (!strict && !!softDisallow?.end))

      const currentStrictlyAllowed = isInterval
        ? isStartAllowed(true) && isEndAllowed(true)
        : isStartAllowed(true) || isEndAllowed(true)

      const currentAllowed =
        currentStrictlyAllowed ||
        (isInterval
          ? isStartAllowed(false) && isEndAllowed(false)
          : isStartAllowed(false) || isEndAllowed(false))

      isStrictlyAllowed &&= currentStrictlyAllowed
      if ((isAllowed &&= currentAllowed) === false) {
        break
      }
    }

    return [isAllowed, isStrictlyAllowed] as const
  }
}

export const CalendarProvider: React.FC<
  React.PropsWithChildren<CalendarProviderOptions>
> = ({
  allowedDates,
  children,
  initialFocusDate = new Date(),
  initialSelectedDate = null,
  locale,
  onDateSelect,
  weekStartsOn = WeekStartsOn.Sunday,
}) => {
  const isDateAllowed = useMemo(
    () => buildDateValidator(allowedDates),
    [allowedDates]
  )

  const tableRef = useRef<HTMLTableElement>(null)

  const { advanceFromKeypress, calendarDateGrid, focusDate, setFocusDate } =
    useCalendarDates({
      initialFocusDate: initialFocusDate,
      weekStartsOn,
    })

  const [selectedDate, setSelectedDate] =
    useState<CalendarContextValue['selectedDate']>(initialSelectedDate)

  const isSelectedDateStrictlyAllowed = useMemo(
    () => (selectedDate ? isDateAllowed(selectedDate)[1] : null),
    [selectedDate, isDateAllowed]
  )

  const selectDate: CalendarContextValue['selectDate'] = (dateString) => {
    if (!dateString) {
      setFocusDate(new Date())
      setSelectedDate(null)
      onDateSelect?.(null)
      return
    }

    // Parse `dateString` as a date in the user's time zone.
    const dateObject = floatingTimestampToDateInZone(
      `${dateString}T00:00`,
      Intl.DateTimeFormat().resolvedOptions().timeZone
    )

    const [isAllowed] = isDateAllowed(dateObject)
    if (!isAllowed) return

    setFocusDate(dateObject)
    setSelectedDate(dateObject)
    onDateSelect?.({
      dateObject,
      dateString,
    })
  }

  return (
    <CalendarContext.Provider
      value={{
        advanceFromKeypress,
        allowedDates,
        calendarDateGrid,
        focusDate,
        locale,
        isDateAllowed,
        isSelectedDateStrictlyAllowed,
        selectDate,
        selectedDate,
        setFocusDate,
        tableRef,
        weekStartsOn,
      }}
    >
      {children}
    </CalendarContext.Provider>
  )
}
CalendarProvider.displayName = 'Lapidary: CalendarProvider ← Calendar'

export const useCalendar = (): CalendarContextValue => {
  const calendarContext = useContext(CalendarContext)

  if (!calendarContext) {
    throw new Error(
      'useCalendar hook must be used within a @opallabs/lapidary <CalendarProvider>.'
    )
  }

  return calendarContext
}
